import {
	ProviderPanelBody,
	providerPanelStatus,
} from "@ext/articleProvider/components/ProviderPanel/ProviderPanelBody";
import { ProviderPanelSearch } from "@ext/articleProvider/components/ProviderPanel/ProviderPanelSearch";
import t from "@ext/localization/locale/translate";
import { PanelList } from "@ui-kit/FloatingPanel";
import type { ToolsTreeNode, ToolsType } from "../../model/types";
import { tui } from "../locale";
import { useWorkspaceToolsActions } from "../store/useWorkspaceToolsActions";
import { ToolsUnavailable } from "../tree/ToolsUnavailable";
import { useToolsRows } from "../tree/useToolsRows";
import { useToolsView } from "../tree/useToolsView";
import { WorkspaceToolsTreeRow } from "./WorkspaceToolsTreeRow";

type WorkspaceToolsPanelContentProps = {
	type: ToolsType;
	query: string;
	onQueryChange: (query: string) => void;
};

const NO_NODES: ToolsTreeNode[] = [];

const PANEL_TEXTS = {
	fragment: {
		search: "fragments-panel-search",
		loading: "fragments-panel-loading",
		notFound: "fragments-panel-empty",
		icon: "puzzle",
		testId: "fragments-empty-state",
	},
	template: {
		search: "templates-panel-search",
		loading: "templates-panel-loading",
		notFound: "templates-panel-empty",
		icon: "layout-template",
		testId: "templates-empty-state",
	},
} as const;

export const WorkspaceToolsPanelContent = ({ type, query, onQueryChange }: WorkspaceToolsPanelContentProps) => {
	const view = useToolsView(type);
	const { openTool } = useWorkspaceToolsActions();
	const nodes = view.kind === "tree" ? view.nodes : NO_NODES;
	const { rows, toggle, searching } = useToolsRows(type, nodes, query);
	const gutter = !searching && rows.some((row) => row.expanded !== null);
	const texts = PANEL_TEXTS[type];

	return (
		<>
			<ProviderPanelSearch onQueryChange={onQueryChange} placeholder={t(texts.search)} query={query} />
			{view.kind === "unavailable" ? (
				<ToolsUnavailable reason={view.reason} />
			) : (
				<ProviderPanelBody
					data-testid={texts.testId}
					empty={{ icon: texts.icon, title: tui(`empty.${type}`), description: tui(`empty-hint.${type}`) }}
					loadingLabel={t(texts.loading)}
					notFound={t(texts.notFound)}
					status={providerPanelStatus(view.kind === "loading", nodes.length, rows.length)}
				>
					<PanelList data-testid="workspace-tools-tree">
						{rows.map((row) => (
							<WorkspaceToolsTreeRow
								depth={row.depth}
								expanded={searching ? null : row.expanded}
								gutter={gutter}
								item={row.node}
								key={row.key}
								onOpen={() => void openTool(type, row.node.id)}
								onToggle={() => toggle(row)}
							/>
						))}
					</PanelList>
				</ProviderPanelBody>
			)}
		</>
	);
};
